import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HighlightOff as HighlightOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import {
  getInventoryCountRequests,
  cancelInventoryCountRequest,
  updateInventoryCountStatus
} from '../../services/inventoryCountService';
import { InventoryCountRequest, InventoryCountStatus } from '../../types/inventoryCount';
import { formatDate } from '../../utils/dateUtils';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Interface for transfer event status
interface TransferEventStatus {
  status: 'success' | 'failed' | 'in_progress' | 'no_events' | 'unknown';
  hasErrors: boolean;
  totalEvents?: number;
  failedEvents?: number;
  successEvents?: number;
}

// مكون الدائرة لحالة الأحداث
interface EventStatusIndicatorProps {
  status: TransferEventStatus | undefined;
  loading: boolean;
  referenceNumber: string;
}

const EventStatusIndicator: React.FC<EventStatusIndicatorProps> = ({ status, loading, referenceNumber }) => {
  if (loading) {
    return (
      <Tooltip title="جاري التحقق من حالة الأحداث...">
        <CircularProgress size={20} />
      </Tooltip>
    );
  }

  if (!status || status.status === 'no_events' || status.status === 'unknown') {
    return (
      <Tooltip title="لا توجد أحداث مسجلة لهذا الطلب">
        <HelpOutlineIcon sx={{ color: 'grey.400', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'success') {
    return (
      <Tooltip title={`جميع الأحداث نجحت (${status.successEvents || 0} حدث)`}>
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'failed' || status.hasErrors) {
    return (
      <Tooltip
        title={`يوجد أخطاء في الأحداث (${status.failedEvents || 0} فاشل من ${status.totalEvents || 0})`}
      >
        <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'in_progress') {
    return (
      <Tooltip title="الطلب قيد التنفيذ">
        <CircularProgress size={20} sx={{ color: 'warning.main' }} />
      </Tooltip>
    );
  }

  return null;
};

const InventoryCountList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<InventoryCountRequest[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState<string>('');
  
  // حالات الأحداث للطلبات (للدوائر الخضراء/الحمراء)
  const [transferStatuses, setTransferStatuses] = useState<Record<string, TransferEventStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);

  // ترجمة حالات الجرد
  const statusLabels: Record<InventoryCountStatus, string> = {
    draft: 'مسودة',
    in_progress: 'قيد التنفيذ',
    completed: 'مكتمل',
    approved: 'معتمد',
    cancelled: 'ملغي'
  };

  // ألوان حالات الجرد
  const statusColors: Record<InventoryCountStatus, string> = {
    draft: 'default',
    in_progress: 'info',
    completed: 'warning',
    approved: 'success',
    cancelled: 'error'
  };

  // جلب قائمة طلبات الجرد
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await getInventoryCountRequests(page, 10);
      if (response.success) {
        setRequests(response.requests);
        setTotalPages(response.totalPages || 1);
      } else {
        setError('حدث خطأ أثناء جلب البيانات');
      }
    } catch (err) {
      console.error('Error fetching inventory count requests:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  // جلب حالات الأحداث للطلبات المعروضة
  useEffect(() => {
    const fetchTransferStatuses = async () => {
      if (requests.length === 0) return;
      
      setLoadingStatuses(true);
      try {
        // جمع أرقام المستندات من الطلبات (نستخدم requestNumber كرقم مرجعي)
        const referenceNumbers = requests
          .map(r => r.requestNumber)
          .filter(Boolean);
        
        if (referenceNumbers.length === 0) {
          setLoadingStatuses(false);
          return;
        }

        // طلب حالات جميع الطلبات دفعة واحدة
        const response = await axios.post(
          `${API_BASE_URL}/inventory-events/transfer-status/bulk`,
          { referenceNumbers },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.data.success) {
          setTransferStatuses(response.data.statuses);
        }
      } catch (error) {
        console.error('خطأ في جلب حالات الطلبات:', error);
        // لا نعرض خطأ للمستخدم لأن هذه ميزة إضافية
      } finally {
        setLoadingStatuses(false);
      }
    };

    // تأخير بسيط لتجنب الطلبات المتزامنة
    const timer = setTimeout(() => {
      fetchTransferStatuses();
    }, 500);

    return () => clearTimeout(timer);
  }, [requests]);

  // جلب البيانات عند تحميل الصفحة أو تغيير رقم الصفحة
  useEffect(() => {
    fetchRequests();
  }, [page]);

  // التعامل مع تغيير رقم الصفحة
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // التعامل مع البحث
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // تصفية قائمة الطلبات حسب البحث
  const filteredRequests = requests.filter(request =>
    request.requestNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (request.warehouseName && request.warehouseName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // فتح حوار تأكيد الحذف
  const handleOpenDeleteDialog = (requestId: string) => {
    setRequestToDelete(requestId);
    setOpenDeleteDialog(true);
  };

  // إغلاق حوار تأكيد الحذف
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRequestToDelete(null);
    setDeleteReason('');
  };

  // تنفيذ حذف الطلب
  const handleConfirmDelete = async () => {
    if (!requestToDelete) return;
    
    setLoading(true);
    const response = await cancelInventoryCountRequest(requestToDelete, deleteReason);
    
    if (response.success) {
      // تحديث القائمة بعد الحذف
      fetchRequests();
      handleCloseDeleteDialog();
    } else {
      console.error('Error deleting inventory count request:', response.message);
      setError(response.message || 'حدث خطأ أثناء حذف الطلب');
      setLoading(false);
    }
  };

  // تغيير حالة الطلب إلى مكتمل
  const handleMarkAsCompleted = async (requestId: string) => {
    try {
      setLoading(true);
      const response = await updateInventoryCountStatus(requestId, 'completed');
      if (response.success) {
        // تحديث القائمة بعد تغيير الحالة
        fetchRequests();
      } else {
        setError('حدث خطأ أثناء تحديث حالة الطلب');
      }
    } catch (err) {
      console.error('Error updating inventory count status:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* شريط الأدوات */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <TextField
          placeholder="بحث في طلبات الجرد..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/inventory-count/new')}
        >
          إنشاء طلب جرد جديد
        </Button>
      </Box>

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* جدول طلبات الجرد */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'primary.main' }}>
            <TableRow>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>حالة الأحداث</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>رقم الطلب</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المستودع</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الحالة</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>تاريخ الإنشاء</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>بواسطة</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    جاري تحميل البيانات...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1">
                    لا توجد طلبات جرد متاحة
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => {
                const referenceNumber = request.requestNumber;
                const eventStatus = transferStatuses[referenceNumber];
                
                return (
                  <TableRow key={request._id} hover>
                    <TableCell align="center">
                      <EventStatusIndicator
                        status={eventStatus}
                        loading={loadingStatuses}
                        referenceNumber={referenceNumber}
                      />
                    </TableCell>
                    <TableCell>{request.requestNumber}</TableCell>
                    <TableCell>{request.warehouseName || '-'}</TableCell>
                    <TableCell>
                    <Chip
                      label={statusLabels[request.status]}
                      color={statusColors[request.status] as any}
                      size="small"
                      sx={{ fontWeight: 'medium' }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(request.createdAt)}</TableCell>
                  <TableCell>{request.createdBy}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="عرض التفاصيل">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => navigate(`/inventory-count/${request._id}`)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {(request.status === 'draft' || request.status === 'in_progress') && (
                        <Tooltip title="حذف الطلب">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleOpenDeleteDialog(request._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {request.status === 'in_progress' && (
                        <Tooltip title="إكمال الجرد">
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() => handleMarkAsCompleted(request._id)}
                          >
                            <CheckCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {request.status === 'completed' && (
                        <>
                          <Tooltip title="اعتماد الجرد">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => navigate(`/inventory-count/${request._id}/approve`)}
                            >
                              <CheckCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="إعادة إلى قيد التنفيذ">
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => updateInventoryCountStatus(request._id, 'in_progress')}
                            >
                              <HighlightOffIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ترقيم الصفحات */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* حوار تأكيد الحذف */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">تأكيد حذف طلب الجرد</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف طلب الجرد؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="سبب الحذف (اختياري)"
            type="text"
            fullWidth
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            تأكيد الحذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryCountList;